import { useApp } from 'ink';
import { flush } from './analytics';
import logger from './logger';

export default function useExit() {
  const { exit } = useApp();

  return (error?: Error) => {
    logger.debug({ error }, 'Exit called');

    /*
      First, destroy connections.
      Why do we need to do this if we're going to call process.exit
      below anyway? Just to be safe in  case a Firestore subscription
      triggers a state update after the React app is exited.
    */
    // Firebase v9+ doesn't require manual termination for most cases
    // If you need to terminate connections, use terminate() from firebase/firestore
    // TODO: Uncomment this when we've been on Firebase v11 for a while and are confident this is no longer needed
    // import { terminate } from "firebase/firestore";
    // terminate(firestore).catch((e) => logger.error(e));

    let timeoutId: ReturnType<typeof setTimeout>;
    Promise.race([
      // flush the analytics to make sure all outstanding events are pushed to segment
      flush(),
      // If it takes longer than 1 second, resolve early to proceed with program exit
      new Promise<void>(
        (resolve) => (timeoutId = setTimeout(() => resolve(), 1000)),
      ),
    ])
      .catch(() => {})
      .finally(() => {
        clearTimeout(timeoutId);
        /*
          Then, exit the React app. We don't pass the error because:
          1. We don't want it displayed. We could give it a dummy error but
            that will output an empty line.
          2. It doesn't exit with the correct code anyway, see
            https://github.com/vadimdemedes/pastel/issues/39.

          Side note: your component must not perform state updates after
          calling this, it will error.

          The try-catch shouldn't be needed here, but let's be safe
        */
        try {
          exit();
        } catch (e) {
          logger.error(e);
          // Ignore
        }

        /*
        Then exit the process.
        It should happen if all connections, etc. are destroyed. We manually
        exit with a code because:
        1. We might not clean up properly, it could fail or take a long time.
        2. The exit function above doesn't exit the CLI with the correct
          code anyway.

        If we had a way to detect when the app exits, we would use that
        and then call process.exit / destroy connections appropriately
        (https://github.com/vadimdemedes/pastel/issues/38).
      */
        setTimeout(() => process.exit(error ? 1 : 0), 10);
      });
  };
}
